/**
 * Amortissement comptable — linéaire (par défaut) ou accéléré (option fiscale).
 *
 * Modèle :
 *   - Véhicules : 5 ans en linéaire, 3 ans si l'option `amortissement_accelere`
 *     est active (équivalent à un coefficient × 5/3 ≈ 1,67 sur la dotation).
 *   - Équipements futurs (dépôts, mobilier) : 10 ans linéaire. Pour l'instant
 *     les dépôts ne sont pas comptabilisés à part — on les modélisera ici
 *     dès qu'ils porteront un prix d'achat individuel.
 *
 * Sortie de `computeFleetDepreciation` :
 *   {
 *     totalDailyDotation : number      // somme des dotations quotidiennes
 *     totalMonthlyDotation : number    // = totalDailyDotation × 30 (approx mois fiscal)
 *     totalAnnualDotation : number     // = totalDailyDotation × 360
 *     items : [{ vehicleId, vehicleName, price, vnc, monthlyDotation, remainingMonths, isAccelerated }]
 *     totalGrossValue : number         // somme des prix d'achat (actif brut)
 *     totalVnc : number                // somme des VNC (actif net comptable)
 *   }
 *
 * Le composant Finance et le slice tax consomment cette même fonction → la
 * VNC affichée à l'écran est strictement la même que celle qui réduit le
 * bénéfice fiscal.
 */

const LINEAR_VEHICLE_MONTHS = 60;       // 5 ans
const ACCELERATED_VEHICLE_MONTHS = 36;  // 3 ans
const DAYS_PER_MONTH_ACCOUNTING = 30;
const DAYS_PER_YEAR_ACCOUNTING = 360;

/**
 * Returns the depreciation duration in months for a vehicle, based on the
 * active tax optimization. Leased vehicles aren't owned → 0 (no depreciation).
 */
export function getVehicleDepreciationMonths(vehicle, activeOptimizations = {}) {
  if (!vehicle || vehicle.isLeased) return 0;
  return activeOptimizations.amortissement_accelere
    ? ACCELERATED_VEHICLE_MONTHS
    : LINEAR_VEHICLE_MONTHS;
}

/**
 * Compute the depreciation status for a single vehicle.
 * Returns `null` if the vehicle is leased (not on the balance sheet).
 */
export function computeVehicleDepreciation(vehicle, totalDays, activeOptimizations = {}) {
  if (!vehicle || vehicle.isLeased || !vehicle.price) return null;
  const months = getVehicleDepreciationMonths(vehicle, activeOptimizations);
  if (months <= 0) return null;
  const purchaseDay = vehicle.purchaseDay || 0;
  const ageDays = Math.max(0, totalDays - purchaseDay);
  const ageMonths = ageDays / DAYS_PER_MONTH_ACCOUNTING;
  const ratio = Math.min(1, ageMonths / months);
  const cumulativeDepreciation = Math.round(vehicle.price * ratio);
  const vnc = Math.max(0, vehicle.price - cumulativeDepreciation);
  const monthlyDotation = ratio < 1 ? Math.round(vehicle.price / months) : 0;
  const dailyDotation = ratio < 1 ? vehicle.price / months / DAYS_PER_MONTH_ACCOUNTING : 0;
  const remainingMonths = Math.max(0, Math.ceil(months - ageMonths));
  return {
    vehicleId: vehicle.id,
    vehicleName: vehicle.name || vehicle.model || 'Véhicule',
    price: vehicle.price,
    cumulativeDepreciation,
    vnc,
    monthlyDotation,
    dailyDotation,
    remainingMonths,
    totalMonths: months,
    isAccelerated: months === ACCELERATED_VEHICLE_MONTHS,
  };
}

/**
 * Aggregate depreciation across the whole owned fleet.
 */
export function computeFleetDepreciation(vehicles = [], totalDays = 0, activeOptimizations = {}) {
  const items = [];
  let totalDailyDotation = 0;
  let totalGrossValue = 0;
  let totalVnc = 0;
  for (const v of vehicles) {
    const item = computeVehicleDepreciation(v, totalDays, activeOptimizations);
    if (!item) continue;
    items.push(item);
    totalDailyDotation += item.dailyDotation;
    totalGrossValue += item.price;
    totalVnc += item.vnc;
  }
  return {
    items,
    totalDailyDotation,
    totalMonthlyDotation: Math.round(totalDailyDotation * DAYS_PER_MONTH_ACCOUNTING),
    totalAnnualDotation: Math.round(totalDailyDotation * DAYS_PER_YEAR_ACCOUNTING),
    totalGrossValue,
    totalVnc,
  };
}
